import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { User, Mail, Lock, Phone, Globe } from "lucide-react";

import { COUNTRY_CODES, formatName, formatPhone } from "@/lib/countryCodes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PatientRegister() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", countryCode: "IN|+91", phone: "", country: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (form.phone && (form.phone.length < 6 || form.phone.length > 15)) { setError("Enter a valid phone number (6-15 digits)"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        name: form.name.trim(),
        email: form.email,
        password: form.password,
        phone: form.phone ? `${form.countryCode.split("|")[1]} ${form.phone}` : "",
        country: form.country
      }, { withCredentials: true });
      await checkAuth();
      toast.success("Account created! Welcome to Flarial MediTours.");
      navigate("/patient");
    } catch (err) {
      const msg = err.response?.data?.detail || "Registration failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="patient-register-page" className="min-h-[80vh] flex items-center justify-center bg-[#F9F9F8] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#00A4A9] flex items-center justify-center mx-auto mb-4">
            <User className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-outfit text-2xl font-medium text-[#03045e]" data-testid="register-title">Patient Registration</h1>
          <p className="text-sm text-slate-500 mt-1">Create your account to track your treatment journey</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="register-form">
          {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3 mb-5" data-testid="register-error">{error}</div>}
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-slate-700 mb-1.5 block">Full Name *</Label>
              <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: formatName(e.target.value) })} className="pl-10" placeholder="John Doe" data-testid="register-name" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-slate-700 mb-1.5 block">Email *</Label>
              <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-10" placeholder="john@example.com" data-testid="register-email" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-slate-700 mb-1.5 block">Password *</Label>
              <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pl-10" placeholder="Min 6 characters" data-testid="register-password" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-slate-700 mb-1.5 block">Phone</Label>
              <div className="flex gap-2">
                <select
                  value={form.countryCode}
                  onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
                  className="h-10 w-24 flex-shrink-0 rounded-lg border border-slate-300 px-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A4A9] bg-white"
                  data-testid="register-country-code"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={`${c.code}|${c.dial}`}>{c.dial} {c.name}</option>
                  ))}
                </select>
                <Input inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} placeholder="9876543210" data-testid="register-phone" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-slate-700 mb-1.5 block">Country</Label>
              <select
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A4A9] bg-white"
                data-testid="register-country"
              >
                <option value="">Select your country</option>
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full mt-6 rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white h-11" data-testid="register-submit-btn">
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
          <p className="text-center text-sm text-slate-500 mt-4">
            Already have an account? <Link to="/patient/login" className="text-[#00A4A9] hover:underline" data-testid="login-link">Sign In</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
